import { css } from '@emotion/react';
import { ReactNode, useContext, useEffect, useMemo } from 'react';
import { Link, matchPath, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import omit from 'lodash/omit';
import t from 'react-translate';
import { AngularContext } from 'react-app';
import { useAppDispatch } from 'redux/store';
import Connections from 'athena/components/mentoring-program/connections';
import Participants from 'athena/components/mentoring-program/participants';
import Guides from 'athena/components/mentoring-program/guides';
import Reporting from 'athena/components/mentoring-program/reporting';
import { LoadingTabs, LoadingContent } from 'athena/components/mentoring-program/loading-ui';
import CreateConnectionFlyoutModal from 'athena/components/mentoring-program/modals/create-connection-flyout-modal';
import ResponsiveTabs, { Tab } from 'athena/components/responsive-tabs';
import setupMentoringProgramEventsListener from 'athena/helpers/mentoring-program-channel-events';
import useMentoringProgramRole from 'athena/hooks/use-mentoring-program-role';
import { darkGray, shadedWhite } from 'athena/styles/colors';
import { getCreateConnectionModalSettings } from 'redux/selectors/mentorship-program-connections';
import { getMentoringProgramGuideById } from 'redux/selectors/mentorship-program-guides';
import { screenMdMin } from 'styles/global_defaults/media-queries';
import { standardSpacing, tripleSpacing } from 'styles/global_defaults/scaffolding';
import GuideModal from '../participant/guide-modal';
import { HomeProps } from '../types';
import MentoringProgramContext from '../../context';
import { withOverflowFlexGrowClass } from '../../constants';
import { config } from '@config/pendo.config.json';


const ProgramAdminHome = ({ setInnerScroll, scrollPercentage }: HomeProps) => {
  const dispatch = useAppDispatch();
  const { isAdmin } = useMentoringProgramRole();
  const location = useLocation();
  const { showModal: showCreateConnectionFlyoutModal } = useSelector(getCreateConnectionModalSettings);

  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const {
    guideId = null,
  } = $state.params || {};
  const mentoringProgramGuide = useSelector(getMentoringProgramGuideById(guideId));

  const { isLoading, countState, mentoringProgram } = useContext(MentoringProgramContext);
  const {
    guidanceEnabled,
  } = mentoringProgram || {};

  const isInnerScrollEnabled = scrollPercentage === 100;
  const tabsContainerTranslate = ((60 / 100) * scrollPercentage) + (isInnerScrollEnabled ? -30 : 0);

  const isTablet = useMediaQuery({
    query: `(max-width: ${screenMdMin}px)`,
  });

  const innerScrollHandler = (e) => {
    setInnerScroll(e.currentTarget.scrollTop);
  };

  const getPadding = () => isInnerScrollEnabled ? 0 : standardSpacing;

  const withOverflowFlexGrowStyles = css`
    .${withOverflowFlexGrowClass} {
      flex: 1;
      min-height: 0;
    }
  `;

  const styles = css`
    ${isTablet && css`
      padding-bottom: ${tripleSpacing}px;
    `};

    .program-content {
      height: calc(100vh - 150px);
      padding: 0 ${getPadding()}px;

      & > .tabs-container {
        top: -${120 - tabsContainerTranslate}px;

        border-radius: 10px;
        height: calc(100% + ${120 - tabsContainerTranslate}px);
        ${withOverflowFlexGrowStyles};

        .tabs-header {
          height: ${tripleSpacing}px;
          border-bottom: 1px solid ${shadedWhite};
        }
      }
    }
  `;

  const tabs: (Tab & { renderContent: () => ReactNode, path: string, allowed: boolean })[] = useMemo(() => {
    // Anchors have styles on hover and on focus by default so I make sure they
    // don't show up in this case with these styles
    const tabStyles = css`
      &:hover, &:focus {
        color: ${darkGray};
      }
    `;

    // Only defining tabs when mentoring program is loaded, one reason to do
    // that is because variable `isAdmin` is only defined correctly when
    // mentoring program is fetched (given they come together in the same API),
    // otherwise it may return null (not loaded yet) being interpreted as falsy
    // causing unintended behavior in hooks of this component that depend on the
    // value of `allowed` property, so making tabs available only when it's
    // safe.
    return mentoringProgram ? [
      {
        allowed: isAdmin,
        id: 'participants',
        path: '/participants',
        text: t.MENTORING_PROGRAMS.PARTICIPANTS.PARTICIPANTS(),
        renderContainer: (props) => <Link to='/participants' css={tabStyles} {...props} />,
        renderContent: () => <Participants onScroll={innerScrollHandler} scrollEnabled={isInnerScrollEnabled} />,
        renderAfterText: () => <span css={css`white-space: pre;`}> ({countState.result?.enrollmentsCount})</span>,
        dataQa: config.pendo.athena.mentorshipProgram.tabs.participantsTab,
      },
      {
        allowed: isAdmin,
        id: 'connections',
        path: '/connections',
        text: t.MENTORING_PROGRAMS.CONNECTIONS.TITLE(),
        renderContainer: (props) => <Link to='/connections' css={tabStyles} {...props} />,
        renderContent: () => <Connections onScroll={innerScrollHandler} scrollEnabled={isInnerScrollEnabled} />,
        renderAfterText: () => <span css={css`white-space: pre;`}> ({countState.result?.connectionsCount})</span>,
        dataQa: config.pendo.athena.mentorshipProgram.tabs.connectionsTab,
      },
      {
        allowed: isAdmin,
        id: 'guides',
        path: '/guides',
        text: t.MENTORING_PROGRAMS.GUIDES.TITLE(),
        renderContainer: (props) => <Link to='/guides' css={tabStyles} {...props} />,
        renderContent: () => <Guides onScroll={innerScrollHandler} scrollEnabled={isInnerScrollEnabled} />,
        dataQa: config.pendo.athena.mentorshipProgram.tabs.guidesTab,
      },
      {
        allowed: isAdmin,
        id: 'reporting',
        path: '/analytics',
        text: t.MENTORING_PROGRAMS.REPORTING.ANALYTICS(),
        renderContainer: (props) => <Link to='/analytics' css={tabStyles} {...props} />,
        renderContent: () => <Reporting onScroll={innerScrollHandler} scrollEnabled={isInnerScrollEnabled} />,
        dataQa: config.pendo.athena.mentorshipProgram.tabs.reportingTab,
      },
    ] : [];
  }, [isAdmin, mentoringProgram, countState.result, isInnerScrollEnabled]);

  const isChangingInstitution = location.hash.includes('change-institution')
    || location.hash.includes('courses');

  const isRoot = !isChangingInstitution && matchPath(location.pathname, {
    path: '/',
  }).isExact;

  useEffect(() => {
    if (isRoot) {
      const defaultTab = tabs.find((tab) => tab.allowed);

      if (defaultTab) {
        $state.go(`mentoring-program-${defaultTab.id}`);
      }
    }
  }, [isRoot, tabs, $state]);

  const currentTab = useMemo(() => tabs.find((tab) => matchPath(location.pathname, {
    path: tab.path,
  })), [tabs, location.pathname]);

  useEffect(() => {
    if (currentTab && !currentTab.allowed) {
      $state.go('mentoring-programs');
    }
  }, [currentTab, $state]);

  useEffect(() => {
    const cleanup = setupMentoringProgramEventsListener(mentoringProgram?.id, dispatch);

    return () => {
      cleanup();
    };
  }, [mentoringProgram?.id, dispatch]);

  return (
    <div css={styles}>
      <div className='program-content bg-color-almost-white'>
        <div className='tabs-container position-relative d-flex flex-column bg-color-white'>
          <div className='tabs-header'>
            {(isLoading || countState.isLoading) ? (
              <LoadingTabs className='h-100' />
            ) : (
              <ResponsiveTabs
                selectedTab={currentTab?.id}
                tabs={tabs.filter((tab) => tab.allowed).map((tab) => omit(tab, ['path', 'renderContent']) as Tab)}
              />
            )}
          </div>
          <div className={`p-4 d-flex flex-column ${withOverflowFlexGrowClass}`}>
            {isLoading ? (
              <LoadingContent />
            ) : (
              <>
                {mentoringProgram && currentTab?.renderContent()}
              </>
            )}
          </div>
        </div>
      </div>
      {showCreateConnectionFlyoutModal && <CreateConnectionFlyoutModal />}
      {guidanceEnabled && mentoringProgramGuide && (
        <GuideModal
          mentoringProgramGuide={mentoringProgramGuide}
        />
      )}
    </div>
  );
};

export default ProgramAdminHome;
